<template>
  <v-dialog v-model="isOpen" max-width="724">
    <div class="ai-tracker-questions-dialog">
      <button type="button" class="ai-tracker-questions-dialog__close" @click="close">
        <i class="material-icons">close</i>
      </button>

      <h2>Add a question to track</h2>

      <div v-if="loading" class="ai-tracker-questions-dialog__state">
        Loading questions...
      </div>
      <div v-else-if="loadError" class="ai-tracker-questions-dialog__state">
        <p>{{ loadError }}</p>
        <button type="button" @click="loadQuestionSettings">Retry</button>
      </div>
      <template v-else>
        <question-picker
          :groups="templateGroups"
          :custom-questions="customQuestions"
          :selected-question-ids="selectedQuestionIds"
          :validation-errors="validationErrors"
          :allow-saved-custom-delete="false"
          @toggle-question="toggleQuestion"
          @add-custom-question="addCustomQuestion"
          @add-custom-questions="addCustomQuestions"
          @update-custom-question="updateCustomQuestion"
          @delete-custom-question="deleteCustomQuestion"
        />

        <div class="ai-tracker-questions-dialog__footer">
          <button
            type="button"
            class="ai-tracker-questions-dialog__primary"
            :disabled="isSaveDisabled"
            @click="saveQuestionSettings"
          >
            {{ primaryActionLabel }}
          </button>
          <p>{{ creditNote }}</p>
          <div v-if="actionError" class="ai-tracker-questions-dialog__error">
            {{ actionError }}
          </div>
        </div>
      </template>
    </div>
  </v-dialog>
</template>

<script>
import QuestionPicker from '../../questions/QuestionPicker.vue'
import {
  getAiVisibilityQuestionSettings,
} from '../../api'

function isCustomQuestion (question) {
  return question && question.source === 'custom'
}

function normalizeQuestion (question) {
  return {
    ...question,
    custom: isCustomQuestion(question),
    selected: question.isEnabled === true,
  }
}

function normalizeGroups (groups) {
  if (!Array.isArray(groups)) {
    return []
  }

  return groups.map((group) => ({
    ...group,
    questions: Array.isArray(group.questions)
      ? group.questions.map(normalizeQuestion)
      : [],
  }))
}

function getSelectedQuestionIds (settings, groups) {
  if (Array.isArray(settings.selectedQuestionIds)) {
    return settings.selectedQuestionIds.slice()
  }

  return groups.reduce((ids, group) => {
    group.questions.forEach((question) => {
      if (question.isEnabled) {
        ids.push(question.id)
      }
    })
    return ids
  }, [])
}

export default {
  name: 'ManageTrackerQuestionsDialog',
  components: {
    QuestionPicker,
  },
  props: {
    siteId: {
      type: [Number, String],
      required: true,
    },
    trackerId: {
      type: [Number, String],
      required: true,
    },
  },
  data () {
    return {
      isOpen: false,
      loading: false,
      saving: false,
      loadError: '',
      actionError: '',
      groups: [],
      selectedQuestionIds: [],
      initialSelectedQuestionIds: [],
      customQuestions: [],
      creditPreview: null,
      validationErrors: {},
      nextClientId: 1,
    }
  },
  computed: {
    templateGroups () {
      return this.groups
        .map((group) => ({
          ...group,
          questions: (group.questions || []).filter((question) => !isCustomQuestion(question)),
        }))
        .filter((group) => group.questions.length)
    },
    selectedQuestionCount () {
      return this.selectedQuestionIds.length
    },
    addedQuestionIds () {
      const initialIds = new Set(this.initialSelectedQuestionIds)
      return this.selectedQuestionIds.filter((id) => !initialIds.has(id))
    },
    addedQuestionCount () {
      return this.addedQuestionIds.length
    },
    primaryActionLabel () {
      return this.saving
        ? 'Staging...'
        : `Stage questions (${this.addedQuestionCount})`
    },
    isSaveDisabled () {
      return this.loading || this.saving || this.addedQuestionCount === 0
    },
    creditNote () {
      const preview = this.creditPreview || {}
      const creditsPerQuestion = preview.creditsPerQuestion || 1
      const unitLabel = preview.unitLabel || 'POP credit'
      const cadence = preview.refreshCadence || 'weekly'
      const unitText = creditsPerQuestion === 1 ? unitLabel : `${unitLabel}s`
      return `Each question adds ${creditsPerQuestion} ${unitText} to your ${cadence} refresh`
    },
  },
  methods: {
    showDialog () {
      this.isOpen = true
      this.loadQuestionSettings()
    },
    close () {
      this.isOpen = false
    },
    resetState () {
      this.loading = false
      this.saving = false
      this.loadError = ''
      this.actionError = ''
      this.groups = []
      this.selectedQuestionIds = []
      this.initialSelectedQuestionIds = []
      this.customQuestions = []
      this.creditPreview = null
      this.validationErrors = {}
    },
    async loadQuestionSettings () {
      this.resetState()
      this.loading = true

      try {
        const response = await getAiVisibilityQuestionSettings(this.siteId, this.trackerId)
        const settings = response.data || {}
        const groups = normalizeGroups(settings.groups)
        this.groups = groups
        this.customQuestions = groups.reduce((questions, group) => (
          questions.concat((group.questions || []).filter(isCustomQuestion))
        ), [])
        this.selectedQuestionIds = getSelectedQuestionIds(settings, groups)
        this.initialSelectedQuestionIds = this.selectedQuestionIds.slice()
        this.creditPreview = settings.creditPreview || null
      } catch (error) {
        this.loadError = 'Could not load tracker questions.'
      } finally {
        this.loading = false
      }
    },
    toggleQuestion (question) {
      const isSelected = this.selectedQuestionIds.includes(question.id)
      this.selectedQuestionIds = isSelected
        ? this.selectedQuestionIds.filter((id) => id !== question.id)
        : this.selectedQuestionIds.concat(question.id)
      this.validationErrors = { ...this.validationErrors, questions: null }
      this.actionError = ''
    },
    addCustomQuestion (text) {
      this.addCustomQuestions([text])
    },
    addCustomQuestions (questions) {
      const newQuestions = questions.map((text) => {
        const clientId = `tmp-${this.nextClientId}`
        this.nextClientId += 1

        return {
          id: clientId,
          clientId,
          text,
          custom: true,
          source: 'custom',
          isEnabled: true,
          selected: true,
          deletable: true,
        }
      })

      this.customQuestions = this.customQuestions.concat(newQuestions)
      this.selectedQuestionIds = this.selectedQuestionIds.concat(newQuestions.map((question) => question.id))
      this.validationErrors = { ...this.validationErrors, questions: null }
      this.actionError = ''
    },
    updateCustomQuestion ({ id, text }) {
      this.customQuestions = this.customQuestions.map((question) => (
        question.id === id ? { ...question, text } : question
      ))
      this.actionError = ''
    },
    deleteCustomQuestion (question) {
      if (!question.deletable) {
        return
      }

      this.customQuestions = this.customQuestions.filter((item) => item.id !== question.id)
      this.selectedQuestionIds = this.selectedQuestionIds.filter((id) => id !== question.id)
      this.actionError = ''
    },
    getDraftPayload () {
      const addedQuestionIdSet = new Set(this.addedQuestionIds)
      const allQuestions = this.groups.reduce((questions, group) => (
        questions.concat(group.questions || [])
      ), []).concat(this.customQuestions)

      return {
        addedQuestionIds: this.addedQuestionIds,
        addedQuestions: allQuestions.filter((question) => addedQuestionIdSet.has(question.id)),
        selectedQuestionIds: this.selectedQuestionIds,
        initialSelectedQuestionIds: this.initialSelectedQuestionIds,
        customQuestions: this.customQuestions.map((question) => {
          if (question.clientId) {
            return {
              clientId: question.clientId,
              text: question.text,
            }
          }

          return {
            id: question.id,
            text: question.text,
          }
        }),
        creditPreview: this.creditPreview,
      }
    },
    async saveQuestionSettings () {
      if (this.isSaveDisabled) {
        return
      }

      this.saving = true
      this.actionError = ''

      try {
        this.$emit('questions-staged', this.getDraftPayload())
        this.close()
      } catch (error) {
        this.actionError = 'Could not stage tracker questions.'
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>

.ai-tracker-questions-dialog {
  position: relative;
  width: 724px;
  max-width: 100%;
  border-radius: 8px;
  background: var(--v-whiteColor-base);
  color: var(--v-mainColor-base);
  padding: 40px 44px 28px;
}

.ai-tracker-questions-dialog__close {
  position: absolute;
  top: 14px;
  right: 14px;
  display: flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  color: var(--v-mainColor-base);
  cursor: pointer;
  padding: 0;

  i {
    font-size: 20px;
  }
}

.ai-tracker-questions-dialog h2 {
  color: var(--v-mainColor-base);
  font-size: 18px;
  font-weight: 800;
  line-height: 1;
  margin: 0 0 20px;
}

.ai-tracker-questions-dialog__state {
  display: flex;
  min-height: 280px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  color: var(--v-mainColor-base);
  font-size: 16px;

  p {
    margin: 0;
  }

  button {
    height: 38px;
    border: 0;
    border-radius: 30px;
    background: var(--v-profileAnchorColor-base);
    color: var(--v-cardBg-base);
    cursor: pointer;
    padding: 0 20px;
  }
}

.ai-tracker-questions-dialog__footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: 34px;
}

.ai-tracker-questions-dialog__primary {
  width: 358px;
  max-width: 100%;
  height: 44px;
  border: 0;
  border-radius: 30px;
  background: var(--v-profileAnchorColor-base);
  color: var(--v-cardBg-base);
  cursor: pointer;
  font-size: 16px;
  line-height: 1.5;
  padding: 10px 20px;

  &:disabled {
    background: var(--v-darkGrayColor-base);
    cursor: not-allowed;
  }
}

.ai-tracker-questions-dialog__footer p {
  color: #D07205;
  font-size: 14px;
  line-height: 1.4;
  margin: 0;
  text-align: center;
}

.ai-tracker-questions-dialog__error {
  color: var(--v-red11Color-base);
  font-size: 12px;
  letter-spacing: 0.12px;
  text-align: center;
}

@media (max-width: 768px) {
  .ai-tracker-questions-dialog {
    padding: 34px 20px 24px;
  }
}
</style>
